import React, { useRef } from "react";
import { AnimatePresence, motion, useMotionValue, useReducedMotion, useSpring, useTransform } from "framer-motion";
import { media } from "../../../data/media";
import { services } from "../../../data/portfolio";
type ServiceMediaCardProps = {
  activeIndex: number;
};

/**
 * The media companion for the stacking service cards. It stays pinned beside the list and
 * flips to the next service's loop whenever the active card changes. Desktop only, since
 * each card carries its own inline loop on smaller screens.
 */
export function ServiceMediaCard({
  activeIndex
}: ServiceMediaCardProps) {
  const reduceMotion = useReducedMotion();
  const frameRef = useRef<HTMLDivElement>(null);
  const service = services[activeIndex] ?? services[0];
  const plate = (media as Record<string, string>)[service.plate] ?? media.archivePlate;
  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);
  const smoothX = useSpring(pointerX, {
    stiffness: 70,
    damping: 20
  });
  const smoothY = useSpring(pointerY, {
    stiffness: 70,
    damping: 20
  });
  const tiltY = useTransform(smoothX, [-0.5, 0.5], [10, -10]);
  const tiltX = useTransform(smoothY, [-0.5, 0.5], [-8, 8]);
  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "mouse" || reduceMotion || !frameRef.current) return;
    const rect = frameRef.current.getBoundingClientRect();
    pointerX.set((event.clientX - rect.left) / rect.width - 0.5);
    pointerY.set((event.clientY - rect.top) / rect.height - 0.5);
  };
  const resetTilt = () => {
    pointerX.set(0);
    pointerY.set(0);
  };
  return <div ref={frameRef} onPointerMove={onPointerMove} onPointerLeave={resetTilt} className="[perspective:1600px]" data-id="element-215" data-mp="14lcyu7i6m7x6">
      <motion.div style={reduceMotion ? undefined : {
      rotateX: tiltX,
      rotateY: tiltY
    }} className="relative aspect-[4/5] w-full [transform-style:preserve-3d]" data-id="element-216" data-mp="19j4sb5yh623h">
        <AnimatePresence mode="wait" initial={false} data-id="element-217" data-mp="1jilrd4j81hqh">
          <motion.div key={service.id} initial={reduceMotion ? {
          opacity: 0
        } : {
          rotateY: -92,
          opacity: 0
        }} animate={reduceMotion ? {
          opacity: 1
        } : {
          rotateY: 0,
          opacity: 1
        }} exit={reduceMotion ? {
          opacity: 0
        } : {
          rotateY: 92,
          opacity: 0
        }} transition={{
          duration: reduceMotion ? 0.3 : 0.62,
          ease: [0.16, 1, 0.3, 1]
        }} className="absolute inset-0 overflow-hidden rounded-[20px] border border-white/10 bg-[#0a0f0e] shadow-[0_50px_120px_-40px_rgba(0,0,0,0.95)] [backface-visibility:hidden]" data-id="element-218" data-mp="ttf1k4rw5iw1">
            {service.videoUrl && !reduceMotion ? <video className="h-full w-full object-cover" src={service.videoUrl} poster={plate} autoPlay muted loop playsInline preload="none" tabIndex={-1} aria-hidden="true" data-id="element-219" data-mp="1mhawp51tb2qp" /> : <img src={plate} alt="" aria-hidden="true" className="loop-plate h-full w-full object-cover" loading="lazy" data-id="element-220" data-mp="1ysbo2kferyua" />}

            <div aria-hidden="true" className="absolute inset-0 bg-[linear-gradient(to_top,rgba(5,8,8,0.82),transparent_58%)]" data-id="element-221" data-mp="23reti5zaw72s" />
            <div aria-hidden="true" className="absolute inset-0 film-grain opacity-[0.16]" data-id="element-222" data-mp="1p17ho93stmb2" />
            <span aria-hidden="true" className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#54f6c6]/70 to-transparent" data-id="element-223" data-mp="1u022thp8nmyc" />

            <div aria-hidden="true" className="absolute inset-x-5 top-5 flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.18em] text-white/75" data-id="element-224" data-mp="1k47a1trawbhu">
              <span data-id="element-225" data-mp="1b30sdhcx0un0">Loop {service.number}</span>
              <span className="flex items-center gap-1.5" data-id="element-226" data-mp="1b3spiwm5v0fe">
                <span className="h-1.5 w-1.5 rounded-full bg-[#54f6c6] dot-blink" data-id="element-227" data-mp="30hp5xlx8simm" />
                {service.videoUrl ? "Playing" : "Plate"}
              </span>
            </div>

            <div aria-hidden="true" className="absolute inset-x-5 bottom-5" data-id="element-228" data-mp="15f26u12nxk9b">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#54f6c6]" data-id="element-229" data-mp="15cy5nqw5ezcq">{service.label}</p>
              <p className="mt-2 text-base font-medium leading-tight tracking-[-0.03em] text-white" data-id="element-230" data-mp="10e4s5ygv38ck">{service.title}</p>
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {/* The active capability's detail, directly beneath its image. */}
      <AnimatePresence mode="wait" initial={false} data-id="element-231" data-mp="mhhgsqo5wfn4">
        <motion.div key={service.id} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: reduceMotion ? 0.2 : 0.38,
        ease: [0.16, 1, 0.3, 1]
      }} className="mt-7" data-id="element-232" data-mp="hoy3lutdzmiu">
          <p className="min-h-[5.25rem] text-sm leading-7 text-white/65 xl:text-[15px] xl:leading-7" data-id="element-233" data-mp="3q882jm7tuwl">{service.description}</p>

          <ul className="mt-5 flex min-h-[6.5rem] flex-wrap content-start gap-2" data-id="element-234" data-mp="3uxayh8u8bx5r">
            {service.capabilities.map(capability => <li key={capability} className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.035] px-3.5 py-2 text-[13px] text-white/80" data-id="element-235" data-mp="2agbipey9pcun">
                <span aria-hidden="true" className="h-1 w-1 shrink-0 rounded-full bg-[#54f6c6]" data-id="element-236" data-mp="2odqfx5uriox6" />
                {capability}
              </li>)}
          </ul>
        </motion.div>
      </AnimatePresence>

      <div aria-hidden="true" className="mt-7 flex items-center justify-between border-t border-white/10 pt-5 font-mono text-[11px] uppercase tracking-[0.16em] text-white/45" data-id="element-237" data-mp="1wm125kwfcozo">
        <span className="flex items-center gap-1.5" data-id="element-238" data-mp="25eukxsb2y6ia">
          {services.map((entry, index) => <span key={entry.id} className={`h-px transition-all duration-500 ${index === activeIndex ? "w-7 bg-[#54f6c6]" : "w-3.5 bg-white/20"}`} data-id="element-239" data-mp="18wy9li095bqe" data-mpi={index} />)}
        </span>
        <span data-id="element-240" data-mp="2g8xn86va45sc">
          {service.number} / {String(services.length).padStart(2, "0")}
        </span>
      </div>
    </div>;
}